/**
 * The popup's staff login.
 *
 * This app had none. It runs on a device at the counter and takes real money
 * on the reader, and the two Terminal routes it calls — `order/terminal/
 * connection-token` and `order/terminal/create-payment-intent` — are the only
 * unauthenticated routes left in the backend that can reach Stripe. They are
 * open *because* this app had nothing to send. (PR #281 put bounds on what an
 * anonymous caller could ask for; this is the part that closes it.)
 *
 * It is the same login the old admin panel and superadmin v2 use
 * (POST users/loginsuperadmin) — one shared staff credential — so there is no
 * new account to manage and no new way in. Only the token is kept.
 *
 * ## The token has to survive the kiosk wipe
 *
 * `resetKioskSession` clears localStorage between every customer, because
 * anything the browser remembers about one customer leaks into the next. The
 * token is not about a customer: it is this device's staff login, the same
 * kind of thing as the check-in defaults in utils/staffPrefs.js. So it is
 * preserved across the wipe, or staff would be signed out after every single
 * order they took.
 *
 * Nothing else may be added to this key. It holds a token and nothing more.
 */

const KEY = "fxry-popup-token";

/** The stored token, or null. Storage can throw in a locked-down browser. */
export const getToken = () => {
  try {
    const token = localStorage.getItem(KEY);
    return typeof token === "string" && token !== "" ? token : null;
  } catch (error) {
    return null;
  }
};

export const setToken = (token) => {
  try {
    if (typeof token === "string" && token !== "") {
      localStorage.setItem(KEY, token);
    }
  } catch (error) {
    console.error("[STAFF AUTH] Failed to save the login:", error);
  }
};

export const clearToken = () => {
  try {
    localStorage.removeItem(KEY);
  } catch (error) {
    console.error("[STAFF AUTH] Failed to clear the login:", error);
  }
};

export const isSignedIn = () => getToken() !== null;

/**
 * The Authorization header for a signed-in device, or {} when there is no
 * token — spread into a request's headers.
 *
 * Empty rather than throwing: these calls are made from the middle of a
 * payment, and a missing header gives the backend's own 401, which the caller
 * turns into "sign in again". A thrown error there would read as a reader
 * fault to whoever is standing at the counter.
 */
export const authHeader = (token = getToken()) =>
  token ? { Authorization: `Bearer ${token}` } : {};

/** True for the backend's "your login is no good" answer, whichever way the
 *  caller surfaces it: axios puts the status on `error.response`, fetch
 *  returns it on the response. */
export const isUnauthorized = (errorOrStatus) => {
  const status =
    typeof errorOrStatus === "number"
      ? errorOrStatus
      : (errorOrStatus?.response?.status ?? errorOrStatus?.status);
  return status === 401 || status === 403;
};
